import React, { FC, useCallback, useContext, useState } from 'react';
import { Button, Col, Modal, ModalBody, ModalFooter, ModalHeader } from 'reactstrap';
import { CashierBadgeData } from '../../../../shared/user/cashier';
import { HotkeyButton } from '../../../components';
import { useTranslation } from '../../../translations';
import { ReprintBadgeModal } from '../ReprintBadge';
import { CashierContext, PrinterPreferenceMode } from '../cashiercontext';

interface PrinterButtonProps {
  readonly badge: CashierBadgeData;
  readonly isReprint?: boolean;
}

export const NfcPrinterButton: FC<PrinterButtonProps> = ({ badge, isReprint }) => {
  const { ts } = useTranslation();
  const { state, dispatch, config } = useContext(CashierContext);
  const [design, setDesign] = useState(config.overrideDesign ?? badge.design);
  const actualDesign = config.overrideDesign ?? design;
  const [reprintReasonModalState, setReprintReasonModalState] = useState(false);
  const nfc = state.nfcScanner;

  const handleNfcWarningNo = useCallback(() => {
    dispatch({
      type: 'NFC_HANDLE_WARNING',
      source: 'nfc',
      status: false,
    });
  }, [dispatch]);

  const handleNfcWarningYes = useCallback(() => {
    dispatch({
      type: 'NFC_HANDLE_WARNING',
      source: 'nfc',
      status: true,
    });
  }, [dispatch]);

  const handlePrintOnly = useCallback(() => {
    if (!state.zebraPrinter) {
      return;
    }

    dispatch({
      type: 'ZEBRA_PRINT',
      source: 'zebra',
      badge: badge.labels[0]!,
      design: badge.design,
      printer: PrinterPreferenceMode.Primary,
    });
  }, [state.zebraPrinter, dispatch, badge.labels, badge.design]);

  if (!nfc) {
    return null;
  }

  return (
    <Col lg={12}>
      {nfc.showNfcWarning && (
        <Modal toggle={handleNfcWarningNo}>
          <ModalHeader toggle={handleNfcWarningNo}>{ts('warning')}</ModalHeader>
          <ModalBody>{ts('cashier_settings.nfc_modal_reprogram_warning')}</ModalBody>
          <ModalFooter>
            <Button color="secondary" onClick={handleNfcWarningNo}>
              {ts('common.no')}
            </Button>
            <Button color="primary" onClick={handleNfcWarningYes}>
              {ts('common.yes')}
            </Button>
          </ModalFooter>
        </Modal>
      )}
      {reprintReasonModalState && (
        <ReprintBadgeModal
          close={() => {
            setReprintReasonModalState(false);
          }}
          currentDesign={actualDesign}
          save={(reason) => {
            setReprintReasonModalState(false);
            dispatch({
              type: 'NFC_PROGRAM',
              source: 'nfc',
              badge,
              reason,
            });
          }}
          setDesign={setDesign}
        />
      )}
      <HotkeyButton
        actionText="print_badge"
        block
        color="primary"
        disabled={false}
        hotkey={' '}
        id="printNfcBadge"
        onClick={() => {
          if (isReprint && config.showReprintModal) {
            setReprintReasonModalState(true);
            return;
          }

          dispatch({
            type: 'NFC_PROGRAM',
            source: 'nfc',
            badge,
          });
        }}
        outline={false}
        requiredKeyPressCount={3}
      >
        {ts('__Program badge')}
      </HotkeyButton>
      <Button block onClick={handlePrintOnly}>
        {ts('__Print Only')}
      </Button>
    </Col>
  );
};
